import axios from 'axios'

// ---------------------------------------------------------------------------
// Token injection
// ---------------------------------------------------------------------------
// getAccessTokenSilently is a React hook, so it cannot be called directly
// inside an Axios interceptor. TokenBridge registers a plain async getter here.

type TokenGetter = () => Promise<string | null | undefined>
type AuthRequiredHandler = () => void | Promise<void>

let _getToken: TokenGetter | null = null
let _onAuthRequired: AuthRequiredHandler | null = null
let authRedirectInFlight = false
let authFailureLogged = false

export type ApiClientError = Error & {
  status?: number
  data?: unknown
}

export class AuthRequiredError extends Error {
  status = 401
  code = 'AUTH_REQUIRED'

  constructor(message = 'Your session expired. Redirecting to sign in.') {
    super(message)
    this.name = 'AuthRequiredError'
  }
}

export function isApiClientError(error: unknown): error is ApiClientError {
  return error instanceof Error && ('status' in error || 'data' in error)
}

export function isAuthRequiredError(error: unknown): error is AuthRequiredError {
  return error instanceof AuthRequiredError
}

export function setTokenGetter(fn: TokenGetter | null): void {
  _getToken = fn
  authFailureLogged = false
}

export function setAuthRequiredHandler(fn: AuthRequiredHandler | null): void {
  _onAuthRequired = fn
  authRedirectInFlight = false
}

function requestReauthentication(): void {
  if (!_onAuthRequired || authRedirectInFlight) return

  authRedirectInFlight = true
  void Promise.resolve(_onAuthRequired()).catch((err) => {
    authRedirectInFlight = false
    console.warn('[apiClient] Failed to start re-authentication:', err)
  })
}

// For raw fetch-based callers (SSE streams) that cannot go through Axios.
// Never returns null: protected requests must not leave without Authorization.
export async function getAccessToken(): Promise<string> {
  if (!_getToken) {
    requestReauthentication()
    throw new AuthRequiredError('Sign in is required before this request can continue.')
  }

  try {
    const token = await _getToken()
    if (!token) {
      throw new AuthRequiredError()
    }
    authFailureLogged = false
    return token
  } catch (err) {
    if (!authFailureLogged) {
      console.warn('[apiClient] Failed to retrieve access token:', err)
      authFailureLogged = true
    }
    requestReauthentication()
    throw err instanceof AuthRequiredError ? err : new AuthRequiredError()
  }
}

// ---------------------------------------------------------------------------
// URL routing
// ---------------------------------------------------------------------------
// Map URL path prefixes to the appropriate microservice base URL.
// Evaluated in order; first match wins.

const ROUTE_MAP: Array<{ prefix: string; baseUrl: string }> = [
  // Identity service
  { prefix: '/api/users',       baseUrl: import.meta.env.VITE_API_URL_IDENTITY },
  { prefix: '/api/auth',        baseUrl: import.meta.env.VITE_API_URL_IDENTITY },
  // Learner service
  { prefix: '/api/learners',    baseUrl: import.meta.env.VITE_API_URL_LEARNER },
  // Evaluation service
  { prefix: '/api/evaluations', baseUrl: import.meta.env.VITE_API_URL_EVALUATION },
  // Journey service
  { prefix: '/api/journey',     baseUrl: import.meta.env.VITE_API_URL_JOURNEY },
  // Content service (guides, games, stories)
  { prefix: '/api/guides',      baseUrl: import.meta.env.VITE_API_URL_CONTENT },
  { prefix: '/api/games',       baseUrl: import.meta.env.VITE_API_URL_CONTENT },
  { prefix: '/api/stories',     baseUrl: import.meta.env.VITE_API_URL_CONTENT },
  { prefix: '/api/content',     baseUrl: import.meta.env.VITE_API_URL_CONTENT },
  // AI service
  { prefix: '/api/ai',          baseUrl: import.meta.env.VITE_API_URL_AI },
  { prefix: '/api/chat',        baseUrl: import.meta.env.VITE_API_URL_AI },
]

function resolveBaseUrl(url: string | undefined): string {
  if (!url) return ''
  for (const { prefix, baseUrl } of ROUTE_MAP) {
    if (url.startsWith(prefix)) return baseUrl
  }
  // Fallback to identity service if no prefix matches.
  console.warn(`[apiClient] No service mapping found for path "${url}", falling back to IDENTITY`)
  return import.meta.env.VITE_API_URL_IDENTITY
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const apiClient = axios.create({
  headers: { 'Content-Type': 'application/json' },
})

// Request interceptor: resolve base URL and attach Bearer token.
apiClient.interceptors.request.use(async (config) => {
  config.baseURL = resolveBaseUrl(config.url)

  const token = await getAccessToken()
  config.headers.Authorization = `Bearer ${token}`

  return config
})

// Response interceptor: normalize error messages.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isAuthRequiredError(error)) {
      return Promise.reject(error)
    }

    // The request left with a token (getAccessToken() succeeded), but the
    // backend itself rejected it — expired/invalid/wrong-audience JWT, or a
    // missing Authorization header that never reached us as a client-side
    // failure. Previously this fell through to the generic branch below and
    // leaked the backend's raw error body (e.g. literally "Unauthorized")
    // straight into the UI instead of prompting re-login (DEP-223). Treat it
    // exactly like a client-side auth failure: kick off re-authentication.
    if (error.response?.status === 401) {
      requestReauthentication()
      return Promise.reject(new AuthRequiredError())
    }

    const message =
      error.response?.data?.message ??
      error.response?.data?.error ??
      error.message ??
      'An unexpected error occurred'
    const wrapped = new Error(message) as ApiClientError
    wrapped.status = error.response?.status
    wrapped.data = error.response?.data
    return Promise.reject(wrapped)
  },
)
